import { ChangeDetectorRef, Component, ErrorHandler, Injector, OnDestroy, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { disabled, FieldTree, form, required, validate } from '@angular/forms/signals'
import { ActivatedRoute } from '@angular/router'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { MatInput } from '@angular/material/input'
import { MatSnackBar } from '@angular/material/snack-bar'

import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FieldErrors, FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { Maa, MaaService } from '../../_jaettu-angular/service/maa.service'

import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'
import { Asiakas, Asiakastiedot, AsiakkaanSopimusversio } from '../../_jaettu-lemonator/model/asiakas'

import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { DateService } from '@lemon/core/service/date.service'
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { AsiakasKopioija } from '../../_angular/service/asiakas/asiakas.kopioija'
import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'

import { MatDialog } from '@angular/material/dialog'
import { ApixReceivedInvoiceConfig } from '@lemon/core-firebase/model/apix'
import { SopimusLisapalvelu } from '@lemon/core-firebase/model/kayttaja'
import { AsiakasJaettuLemonaidService } from '@lemon/core-firebase/service/asiakas-jaettu-lemonaid.service'
import { TuettuKieli } from '@lemon/core/model/common'
import { DebugService } from 'app/_angular/service/debug.service'
import { AsiakkaanSopimuskausiService } from 'app/_jaettu-lemonator/service/sopimuskausi.service'
import { combineLatest, firstValueFrom, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { map, switchMap, takeUntil, takeWhile } from 'rxjs/operators'
import { KirjanpitajanTiimiService } from '../../_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { AsiakasAsetuksetGoogleSheetLinkkiDialog, AsiakasAsetuksetGoogleSheetLinkkiDialogData } from './google-sheet-linkki.dialog'

interface AsiakastiedotLomake {
  nimi: string
  ytunnus: string
  katuosoite: string
  maa: string | null
  postinro: string
  postitoimipaikka: string

  laskunKieli: TuettuKieli | null
  kuvausKuvaus: string | null
  kuvausAloitus: string | null
  kuvausHuomioita: string | null

  palaveriOnkoPidetty: boolean | null
  palaveriTyyppi: 'puhelin' | 'Google Meet' | 'toimisto' | null
  palaveriPvm: Date | null

  tositemaaratKuitit: number | null
  tositemaaratMyyntilaskut: number | null
  tositemaaratMyyntiraportit: number | null
  tositemaaratPalkkalaskelmat: number | null
  tositemaaratMuuta: string | null

  maksutavatLasku: boolean | null
  maksutavatKorttipaate: boolean | null
  maksutavatKorttipaateLisa: string | null
  maksutavatVerkkokauppa: boolean | null
  maksutavatVerkkokauppaMaksutavat: string | null
  maksutavatKateinen: boolean | null
  maksutavatTilitykset: boolean | null
  maksutavatTilityksetMita: string | null

  pankkiPankki: string | null
  pankkiYhteys: boolean | null
  pankkiEiTilia: boolean | null

  auto: boolean | null
  autoTyyppi: 'henkilo' | 'paketti' | null
  autoAjo: 'enemmanyksityis' | 'enemmantyo' | 'kokonaantyo' | null
  autoLisa: string | null

  sijoitus: boolean | null
  sijoitusLisa: string | null

  ohjelmisto: 'lemonaid' | 'holvi' | 'molemmat' | null
  ohjelmistoLisa: string | null

  palkatLisa: string | null

  ossRekisterissa: boolean | null
  estaAlvKasittely: boolean | null
  testiasiakas: boolean | null
  tilinpaatosRekisteroidaanBruttotuloslaskelmalla: boolean | null

  saleDoneBy: string
}

@Component({
  templateUrl: './asiakastiedot.component.html',
  styleUrls: ['./asiakastiedot.component.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakasAsetuksetAsiakastiedotComponent implements OnInit, OnDestroy {
  @ViewChild(MatInput, { static: true }) nimiInput: MatInput

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  dialogOpen: boolean = false

  suodatetutMaat: ReplaySubject<Maa[]> = new ReplaySubject(1)

  asiakasUriObservable: Observable<string>
  asiakasEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  asiakkaanApixTiedotObservable: Observable<Partial<ApixReceivedInvoiceConfig>>
  readonly sopimuksenLisapalvelutMap = signal<AsiakkaanSopimusversio['lisapalvelut']>(undefined)

  private readonly _injector = inject(Injector)
  private readonly _palaveriPakollinen = signal(false)
  private readonly _tekstikenttienKasittelijat = new Map<object, (arvo: string | null) => void>()
  readonly ytunnuksenTupla = signal<{ asiakasAvain: string; nimi: string } | null>(null)

  lomake = form(
    signal<AsiakastiedotLomake>({
      nimi: '',
      ytunnus: '',
      katuosoite: '',
      maa: '',
      postinro: '',
      postitoimipaikka: '',

      laskunKieli: null,

      kuvausKuvaus: null,
      kuvausAloitus: null,
      kuvausHuomioita: null,

      palaveriOnkoPidetty: null,
      palaveriTyyppi: null,
      palaveriPvm: null,

      tositemaaratKuitit: null,
      tositemaaratMyyntilaskut: null,
      tositemaaratMyyntiraportit: null,
      tositemaaratPalkkalaskelmat: null,
      tositemaaratMuuta: null,

      maksutavatLasku: null,
      maksutavatKorttipaate: null,
      maksutavatKorttipaateLisa: null,
      maksutavatVerkkokauppa: null,
      maksutavatVerkkokauppaMaksutavat: null,
      maksutavatKateinen: null,
      maksutavatTilitykset: null,
      maksutavatTilityksetMita: null,

      pankkiPankki: null,
      pankkiYhteys: null,
      pankkiEiTilia: null,

      auto: null,
      autoTyyppi: null,
      autoAjo: null,
      autoLisa: null,

      sijoitus: null,
      sijoitusLisa: null,

      ohjelmisto: null,
      ohjelmistoLisa: null,

      palkatLisa: null,

      ossRekisterissa: null,
      estaAlvKasittely: null,
      testiasiakas: null,
      tilinpaatosRekisteroidaanBruttotuloslaskelmalla: null,

      saleDoneBy: '',
    }),
    polku => {
      required(polku.nimi)
      required(polku.ytunnus)
      validate(polku.ytunnus, () => (this.ytunnuksenTupla() ? { kind: 'tupla' } : null))
      required(polku.palaveriTyyppi, { when: () => this._palaveriPakollinen() })
      required(polku.palaveriPvm, { when: () => this._palaveriPakollinen() })
      disabled(polku.saleDoneBy)
    },
  )
  fieldErrors: FieldErrors = {}

  readonly laskureskontraVoimassa = signal<boolean>(undefined)

  protected readonly SopimusLisapalvelu = SopimusLisapalvelu

  isAdminObservable: Observable<boolean>
  asiakas: Asiakas = null
  inputname = 'asdfewqrphoki' + Math.random()
  tiedot: Asiakastiedot = null
  readonly ytunnusVirhe = signal(false)

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _route: ActivatedRoute,
    private _asiakasService: AsiakasService,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
    private _asiakasKopioija: AsiakasKopioija,
    private _copyService: KopioijaPalvelu,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _maaService: MaaService,
    private _firebase: FirebaseLemonator,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _timestampService: TimestampProviderBase,
    private _dateService: DateService,
    private _codeCheckService: CodeCheckService,
    private _asiakasUriService: AsiakasUriService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _debugService: DebugService,
    private _asiakasJaettuLemonaidService: AsiakasJaettuLemonaidService,
    private _dialog: MatDialog,
    private _sopimuskausiService: AsiakkaanSopimuskausiService,
  ) {
    this.asiakas = this._asiakasKopioija.annaUusiAsiakas()
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnInit() {
    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.asiakasUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return 'asiakkaat/' + avainTiedot.avain
        }
        return ''
      }),
    )
    this.asiakasEncodedUriObservable = this.asiakasUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )

    this.isAdminObservable = this._kirjautunutKayttajaService.kirjanpitajanAnnetaanMuokataMitaTahansaKirjauksiaObservable

    this.asiakkaanApixTiedotObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(avainTiedot => {
        if (avainTiedot?.avain) {
          const uri = 'customers/' + avainTiedot.avain + '/apix-received-invoice-config/' + avainTiedot.avain
          return this._firebaseLemonaid
            .firestoreDoc<ApixReceivedInvoiceConfig>(uri)
            .listen()
            .pipe(map(data => data || {}))
        }
        return of<Partial<ApixReceivedInvoiceConfig>>({})
      }),
    )

    const reskontraTyyppiObservable = combineLatest([this._asiakasService.nykyinenAsiakasObservable, this._asiakasService.nykyisenAsiakkaanLaskuasetuksetObservable]).pipe(
      map(([asiakas, laskuasetukset]) => {
        if (!asiakas || !laskuasetukset) {
          return null
        }
        return this._asiakasJaettuLemonaidService.annaVoimassaOlevaReskontranTyyppi(asiakas, laskuasetukset)
      }),
    )

    reskontraTyyppiObservable.pipe(takeWhile(tyyppi => !tyyppi, true)).subscribe(reskontraTyyppi => {
      this.laskureskontraVoimassa.set(reskontraTyyppi === 'laskureskontra')
    })

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      if (!asiakas?.sopimuskaudet?.length) {
        return
      }
      const sopimuskausi = this._sopimuskausiService.annaKausiPaivamaaralle(asiakas.sopimuskaudet, this._dateService.currentLocalDate())
      if (!sopimuskausi) {
        return
      }
      const sopimusversio = this._sopimuskausiService.annaVoimassaOlevaSopimusversio(sopimuskausi)
      this.sopimuksenLisapalvelutMap.set(sopimusversio?.lisapalvelut || {})
    })

    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    // Bind to changes
    this._tilaaTekstikentta(this.lomake.nimi, value => {
      if (this.asiakas) {
        this.asiakas.nimi = this._validationService.processValue(value)
      }
    })
    this._tilaaTekstikentta(this.lomake.ytunnus, value => {
      this.ytunnuksenTupla.set(null)
      if (this.asiakas) {
        this.asiakas.ytunnus = this._validationService.processValue(value)
        this.ytunnusVirhe.set(!!this.asiakas.ytunnus && !this._codeCheckService.isValidYTunnus(this.asiakas.ytunnus))
      }
    })
    this._tilaaTekstikentta(this.lomake.katuosoite, value => {
      if (this.asiakas) {
        this.asiakas.katuosoite = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.maa).subscribe(value => {
      if (this.asiakas) {
        this.asiakas.maa = this._validationService.processValue(value)
      }
    })
    this._tilaaTekstikentta(this.lomake.postinro, value => {
      if (this.asiakas) {
        this.asiakas.postinro = this._validationService.processValue(value)
      }
    })
    this._tilaaTekstikentta(this.lomake.postitoimipaikka, value => {
      if (this.asiakas) {
        this.asiakas.postitmp = this._validationService.processValue(value)
      }
    })

    muutokset(this.lomake.laskunKieli).subscribe(value => {
      if (this.asiakas) {
        this.asiakas.laskunKieli = value || null
      }
    })

    muutokset(this.lomake.kuvausKuvaus).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.kuvausKuvaus = value || null
      }
    })
    muutokset(this.lomake.kuvausAloitus).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.kuvausAloitus = value || null
      }
    })
    muutokset(this.lomake.kuvausHuomioita).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.kuvausHuomioita = value || null
      }
    })

    muutokset(this.lomake.palaveriOnkoPidetty)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        if (this.asiakas) {
          this.asiakas.onkoPalaveriPidetty = this._validationService.processValue(value)
        }
        // if (value) {
        //   this.form.get('palaveriPvm').setValidators([Validators.required])
        //   this.form.get('palaveriTyyppi').setValidators([Validators.required])
        // } else {
        //   this.form.get('palaveriPvm').clearValidators()
        //   this.form.get('palaveriTyyppi').clearValidators()
        // }
        // this.form.get('palaveriPvm').updateValueAndValidity()
        // this.form.get('palaveriTyyppi').updateValueAndValidity()
      })
    muutokset(this.lomake.palaveriTyyppi).subscribe(value => {
      if (this.asiakas) {
        this.asiakas.palaveriTyyppi = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.palaveriPvm).subscribe(value => {
      if (this.asiakas) {
        this.asiakas.palaveriPidettyPvm = this._timestampService.dateToTimestamp(value)
      }
    })

    muutokset(this.lomake.tositemaaratKuitit).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.tositemaaratKuitit = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.tositemaaratMyyntilaskut).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.tositemaaratMyyntilaskut = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.tositemaaratMyyntiraportit).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.tositemaaratMyyntiraportit = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.tositemaaratPalkkalaskelmat).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.tositemaaratPalkkalaskelmat = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.tositemaaratMuuta).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.tositemaaratMuuta = this._validationService.processValue(value)
      }
    })

    muutokset(this.lomake.maksutavatLasku).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.maksutavatLasku = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.maksutavatKorttipaate).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.maksutavatKorttipaate = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.maksutavatKorttipaateLisa).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.maksutavatKorttipaateLisa = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.maksutavatVerkkokauppa).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.maksutavatVerkkokauppa = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.maksutavatVerkkokauppaMaksutavat).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.maksutavatVerkkokauppaMaksutavat = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.maksutavatKateinen).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.maksutavatKateinen = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.maksutavatTilitykset).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.maksutavatTilitykset = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.maksutavatTilityksetMita).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.maksutavatTilityksetMita = this._validationService.processValue(value)
      }
    })

    muutokset(this.lomake.pankkiPankki).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.pankkiPankki = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.pankkiYhteys).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.pankkiYhteys = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.pankkiEiTilia).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.pankkiEiTilia = this._validationService.processValue(value)
      }
    })

    muutokset(this.lomake.auto).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.auto = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.autoTyyppi).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.autoTyyppi = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.autoAjo).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.autoAjo = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.autoLisa).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.autoLisa = this._validationService.processValue(value)
      }
    })

    muutokset(this.lomake.sijoitus).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.sijoitus = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.sijoitusLisa).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.sijoitusLisa = this._validationService.processValue(value)
      }
    })

    muutokset(this.lomake.ohjelmisto).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.ohjelmisto = this._validationService.processValue(value)
      }
    })
    muutokset(this.lomake.ohjelmistoLisa).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.ohjelmistoLisa = this._validationService.processValue(value)
      }
    })

    muutokset(this.lomake.palkatLisa).subscribe(value => {
      if (this.tiedot) {
        this.tiedot.palkatLisa = this._validationService.processValue(value)
      }
    })

    muutokset(this.lomake.ossRekisterissa)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        if (this.asiakas) {
          if (value) {
            this.asiakas.ossRekisterissa = 1
          } else {
            delete this.asiakas.ossRekisterissa
          }
        }
      })
    muutokset(this.lomake.estaAlvKasittely)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        if (this.asiakas) {
          if (value) {
            this.asiakas.kasittelePalkatAlvittomana = 1
          } else {
            delete this.asiakas.kasittelePalkatAlvittomana
          }
        }
      })

    muutokset(this.lomake.testiasiakas)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        if (this.asiakas) {
          if (value) {
            this.asiakas.testiasiakas = 1
          } else {
            delete this.asiakas.testiasiakas
          }
        }
      })

    muutokset(this.lomake.tilinpaatosRekisteroidaanBruttotuloslaskelmalla)
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        if (this.asiakas) {
          if (value) {
            this.asiakas.tilinpaatosRekisteroidaanBruttotuloslaskelmalla = 1
          } else {
            delete this.asiakas.tilinpaatosRekisteroidaanBruttotuloslaskelmalla
          }
        }
      })

    // muutokset(this.lomake.riskiarvio).pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => { if (this.tiedot) { this.tiedot.riskiarvio = this._validationService.processValue(value) } })
    // muutokset(this.lomake.riskiarvioLisa).pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => { if (this.tiedot) { this.tiedot.risikiarvioLisa = this._validationService.processValue(value) } })

    this._maaService.kaikkiMaatObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(maat => {
      this.suodatetutMaat.next(maat)
    })

    const asiakasJaAsiakastiedotObservable: Observable<{ asiakas: Asiakas; tiedot: Asiakastiedot }> = this._asiakasService.nykyinenAsiakasObservable.pipe(
      switchMap(asiakas => {
        if (asiakas) {
          return this._firebase
            .firestoreDoc<Asiakastiedot>(this._asiakasUriService.annaAsiakastiedotUri(asiakas.avain))
            .listen()
            .pipe(
              map(asiakastiedot => {
                if (asiakas) {
                  if (asiakastiedot) {
                    return { asiakas: asiakas, tiedot: asiakastiedot }
                  }
                  const t: Asiakastiedot = {
                    asiakasAvain: null,

                    kuvausKuvaus: null,
                    kuvausAloitus: null,
                    kuvausHuomioita: null,

                    tositemaaratKuitit: null,
                    tositemaaratMyyntilaskut: null,
                    tositemaaratMyyntiraportit: null,
                    tositemaaratPalkkalaskelmat: null,
                    tositemaaratMuuta: null,

                    maksutavatLasku: null,
                    maksutavatKorttipaate: null,
                    maksutavatKorttipaateLisa: null,
                    maksutavatVerkkokauppa: null,
                    maksutavatVerkkokauppaMaksutavat: null,
                    maksutavatKateinen: null,
                    maksutavatTilitykset: null,
                    maksutavatTilityksetMita: null,

                    pankkiPankki: null,
                    pankkiYhteys: null,
                    pankkiEiTilia: null,

                    auto: null,
                    autoTyyppi: null,
                    autoAjo: null,
                    autoLisa: null,

                    sijoitus: null,
                    sijoitusLisa: null,

                    ohjelmisto: null,
                    ohjelmistoLisa: null,

                    palkatLisa: null,

                    // riskiarvio: null,
                    // risikiarvioLisa: null,
                    riskiarvioStatus: 'puuttuu',

                    avain: null,
                    luoja: null,
                    luotu: null,
                    paivittaja: null,
                    paivitetty: null,
                    poistettu: null,
                  }
                  return { asiakas: asiakas, tiedot: t }
                }
                return null
              }),
            )
        }
        return of(null)
      }),
    )

    asiakasJaAsiakastiedotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakasJaTiedot => {
      if (asiakasJaTiedot) {
        this.asiakas = this._asiakasKopioija.kopioiAsiakas(asiakasJaTiedot.asiakas)
        this.tiedot = this._copyService.cloneObjectDeep(asiakasJaTiedot.tiedot)
        this.alustaLomakkeenTiedot(this.asiakas, this.tiedot)
        this._changeDetectorRef.markForCheck()
      }
      setTimeout(() => {
        if (!this.dialogOpen) {
          this.nimiInput.focus()
          this._changeDetectorRef.markForCheck()
        }
      }, 10)
    })
  }

  async muokkaaGoogleSheetLinkkia() {
    this.dialogOpen = true
    const dialogRef = this._dialog.open<AsiakasAsetuksetGoogleSheetLinkkiDialog, AsiakasAsetuksetGoogleSheetLinkkiDialogData, AsiakasAsetuksetGoogleSheetLinkkiDialogData>(
      AsiakasAsetuksetGoogleSheetLinkkiDialog,
      {
        data: { sheetLink: this.asiakas.sheetLink },
      },
    )
    const tulos = await firstValueFrom(dialogRef.afterClosed())
    if (tulos) {
      this.dialogOpen = false
      this.asiakas.sheetLink = tulos.sheetLink
      this._changeDetectorRef.markForCheck()

      this._asiakasService
        .paivitaAsiakasJaTiedot(this.asiakas, this.tiedot, 'tilikaudet-eivat-voineet-muuttua', false, false)
        .then(() => {
          this._asiakasService.asetaNykyinenAsiakas(this.asiakas)
          this._snackbar.open('Google Sheet -linkki tallennettiin onnistuneesti.', 'OK', {
            duration: 5000,
            verticalPosition: 'top',
          })
        })
        .catch(err => {
          this._errorHandler.handleError(err)
        })
    }
    this.dialogOpen = false
  }

  private alustaLomakkeenTiedot(asiakas: Asiakas, tiedot: Asiakastiedot) {
    const kasittele = <T>(arvo: T): T => this._validationService.processValue(arvo)
    this._asetaTekstikentta(this.lomake.nimi, kasittele(asiakas.nimi))
    this._asetaTekstikentta(this.lomake.ytunnus, kasittele(asiakas.ytunnus))
    this._asetaTekstikentta(this.lomake.katuosoite, kasittele(asiakas.katuosoite))
    asetaArvo(this.lomake.maa, kasittele(asiakas.maa))
    this._asetaTekstikentta(this.lomake.postinro, kasittele(asiakas.postinro))
    this._asetaTekstikentta(this.lomake.postitoimipaikka, kasittele(asiakas.postitmp))

    asetaArvo(this.lomake.laskunKieli, kasittele(asiakas.laskunKieli || 'fi'))

    asetaArvo(this.lomake.kuvausKuvaus, tiedot.kuvausKuvaus || '')
    asetaArvo(this.lomake.kuvausAloitus, tiedot.kuvausAloitus || '')
    asetaArvo(this.lomake.kuvausHuomioita, tiedot.kuvausHuomioita || '')

    asetaArvo(this.lomake.palaveriTyyppi, kasittele(asiakas.palaveriTyyppi))
    asetaArvo(this.lomake.palaveriPvm, this._dateService.timestampToDate(asiakas.palaveriPidettyPvm))

    const pidetty = kasittele(asiakas.onkoPalaveriPidetty)
    asetaArvo(this.lomake.palaveriOnkoPidetty, pidetty)
    this._palaveriPakollinen.set(!!pidetty)

    asetaArvo(this.lomake.tositemaaratKuitit, kasittele(tiedot.tositemaaratKuitit))
    asetaArvo(this.lomake.tositemaaratMyyntilaskut, kasittele(tiedot.tositemaaratMyyntilaskut))
    asetaArvo(this.lomake.tositemaaratMyyntiraportit, kasittele(tiedot.tositemaaratMyyntiraportit))
    asetaArvo(this.lomake.tositemaaratPalkkalaskelmat, kasittele(tiedot.tositemaaratPalkkalaskelmat))
    asetaArvo(this.lomake.tositemaaratMuuta, kasittele(tiedot.tositemaaratMuuta))

    asetaArvo(this.lomake.maksutavatLasku, kasittele(tiedot.maksutavatLasku))
    asetaArvo(this.lomake.maksutavatKorttipaate, kasittele(tiedot.maksutavatKorttipaate))
    asetaArvo(this.lomake.maksutavatKorttipaateLisa, kasittele(tiedot.maksutavatKorttipaateLisa))
    asetaArvo(this.lomake.maksutavatVerkkokauppa, kasittele(tiedot.maksutavatVerkkokauppa))
    asetaArvo(this.lomake.maksutavatVerkkokauppaMaksutavat, kasittele(tiedot.maksutavatVerkkokauppaMaksutavat))
    asetaArvo(this.lomake.maksutavatTilitykset, kasittele(tiedot.maksutavatTilitykset))
    asetaArvo(this.lomake.maksutavatTilityksetMita, kasittele(tiedot.maksutavatTilityksetMita))
    asetaArvo(this.lomake.maksutavatKateinen, kasittele(tiedot.maksutavatKateinen))

    asetaArvo(this.lomake.pankkiPankki, kasittele(tiedot.pankkiPankki))
    asetaArvo(this.lomake.pankkiYhteys, kasittele(tiedot.pankkiYhteys))
    asetaArvo(this.lomake.pankkiEiTilia, kasittele(tiedot.pankkiEiTilia))

    asetaArvo(this.lomake.auto, kasittele(tiedot.auto))
    asetaArvo(this.lomake.autoTyyppi, kasittele(tiedot.autoTyyppi))
    asetaArvo(this.lomake.autoAjo, kasittele(tiedot.autoAjo))
    asetaArvo(this.lomake.autoLisa, kasittele(tiedot.autoLisa))

    asetaArvo(this.lomake.sijoitus, kasittele(tiedot.sijoitus))
    asetaArvo(this.lomake.sijoitusLisa, kasittele(tiedot.sijoitusLisa))

    asetaArvo(this.lomake.ohjelmisto, kasittele(tiedot.ohjelmisto))
    asetaArvo(this.lomake.ohjelmistoLisa, kasittele(tiedot.ohjelmistoLisa))

    asetaArvo(this.lomake.palkatLisa, kasittele(tiedot.palkatLisa))

    asetaArvo(this.lomake.ossRekisterissa, !!asiakas.ossRekisterissa)
    asetaArvo(this.lomake.estaAlvKasittely, !!asiakas.kasittelePalkatAlvittomana)
    asetaArvo(this.lomake.testiasiakas, !!asiakas.testiasiakas)
    asetaArvo(this.lomake.tilinpaatosRekisteroidaanBruttotuloslaskelmalla, !!asiakas.tilinpaatosRekisteroidaanBruttotuloslaskelmalla)

    if (asiakas.saleDoneBy) {
      const salesTeamOptions = this._kirjanpitajanTiimiService.getHardcodedSalesTeamOptions()
      const myyja = salesTeamOptions.find(option => option.avain === asiakas.saleDoneBy)
      asetaArvo(this.lomake.saleDoneBy, myyja ? myyja.nimi : 'Muu')
    }
  }

  async save() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      this._validationService.naytaEnsimmainenVirhe()
      return
    }

    this._ladataanService.aloitaLataaminen()

    const olemassaOleva = await this._asiakasService.annaAsiakkaatYtunnuksella(this.asiakas.ytunnus).then(vastaus => {
      if (vastaus && vastaus.loytyneet) {
        for (const asiakas of vastaus.loytyneet) {
          if (asiakas.asiakasAvain !== this.asiakas.avain) {
            return asiakas
          }
        }
      }
      return null
    })

    if (olemassaOleva) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      this.ytunnuksenTupla.set(olemassaOleva)
      this._ladataanService.lopetaLataaminen()
      return
    }

    this._asiakasService
      .paivitaAsiakasJaTiedot(this.asiakas, this.tiedot, 'tilikaudet-eivat-voineet-muuttua', false, false)
      .then(() => {
        this._asiakasService.asetaNykyinenAsiakas(this.asiakas)
        this._ladataanService.lopetaLataaminen()
        this._snackbar.open('Asiakas tallennettiin onnistuneesti.', 'OK', {
          duration: 5000,
          verticalPosition: 'top',
        })
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
      })
  }

  private _tilaaTekstikentta(kentta: FieldTree<string>, kasittelija: (arvo: string | null) => void) {
    this._tekstikenttienKasittelijat.set(kentta, kasittelija)
    arvonMuutokset(kentta, { injector: this._injector }).subscribe(kasittelija)
  }

  // Tekstikentän malliarvo ei voi olla null, mutta tilaaja saa asetetun arvon sellaisenaan kuten reaktiivisen lomakkeen setValue-kutsussa.
  private _asetaTekstikentta(kentta: FieldTree<string>, arvo: string | null) {
    asetaArvo(kentta, arvo ?? '', { ilmoita: false })
    this._tekstikenttienKasittelijat.get(kentta)?.(arvo)
  }

  naytetaankoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }

  name(): string {
    return '' + Math.random()
  }
}
